import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Business as BusinessIcon,
  AccountBalance as BrokerIcon,
  Refresh as RefreshIcon,
  AccountBalance as LedgerIcon,
  ViewModule,
  ViewList,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import {
  useGetCustomersQuery,
  useGetCustomerGroupsQuery,
  useDeleteCustomerMutation,
  useBulkExportCustomersMutation,
  useBulkUpdateCustomerStatusMutation,
} from '../store/services/customerApi'
import { Customer, CustomerType } from '../types/customer'
import BulkActionBar, { getCustomerBulkActions } from '../components/ui/BulkActionBar'
import ProgressManager from '../components/ui/ProgressManager'
import useBulkOperations from '../hooks/useBulkOperations'
import PartyCard from '../components/party/PartyCard'
import AddressEditModal from '../components/party/AddressEditModal'
import NotificationEditModal from '../components/party/NotificationEditModal'
import ShippingAddressesModal from '../components/party/ShippingAddressesModal'
import { PartyAddressService } from '../services/partyAddressService'
import * as notificationService from '../services/partyNotificationService'

const CustomersMUI: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  // Deactivation dialog state
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [customerToDeactivate, setCustomerToDeactivate] = useState<Customer | null>(null)
  const [customerBalance, setCustomerBalance] = useState<number>(0)
  
  // Bulk operations
  const { 
    operations, 
    executeBulkOperation, 
    clearCompletedOperations, 
    clearAllOperations 
  } = useBulkOperations()
  
  // Selection state
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([])
  
  // View mode state
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table')
  const [currentTab, setCurrentTab] = useState(0)
  
  // Modal states
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const [notificationModalOpen, setNotificationModalOpen] = useState(false)
  const [shippingModalOpen, setShippingModalOpen] = useState(false)
  const [selectedParty, setSelectedParty] = useState<any>(null)
  const [addressType, setAddressType] = useState<'billing' | 'shipping'>('billing')
  const [editingAddress, setEditingAddress] = useState<any>(null)
  
  // Customer state
  const [customerPage, setCustomerPage] = useState(0)
  const [customerPageSize, setCustomerPageSize] = useState(25)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('')
  const [customerGroupFilter, setCustomerGroupFilter] = useState<string>('')
  const [customerActiveFilter, setCustomerActiveFilter] = useState<string>('')

  // API queries
  const {
    data: customersData,
    isLoading: customersLoading,
    error: customersError,
    refetch: refetchCustomers,
  } = useGetCustomersQuery({
    page: customerPage + 1,
    per_page: customerPageSize,
    search: customerSearch || undefined,
    customer_type: customerTypeFilter || undefined,
    customer_group_id: customerGroupFilter || undefined,
    is_active: customerActiveFilter ? customerActiveFilter === 'true' : undefined,
  })

  // Get customer groups for filter dropdown
  const {
    data: groupsData,
  } = useGetCustomerGroupsQuery({
    page: 1,
    per_page: 100,
  })

  // Mutations
  const [deleteCustomer] = useDeleteCustomerMutation()
  const [bulkExportCustomers] = useBulkExportCustomersMutation()
  const [bulkUpdateCustomerStatus] = useBulkUpdateCustomerStatusMutation()

  // Customer type icons and labels
  const getCustomerTypeIcon = (type: CustomerType) => {
    switch (type) {
      case CustomerType.RETAIL:
        return <PersonIcon />
      case CustomerType.RESELLER:
        return <StoreIcon />
      case CustomerType.WHOLESALER:
        return <BusinessIcon />
      case CustomerType.BROKER:
        return <BrokerIcon />
      default:
        return <PersonIcon />
    }
  }

  const getCustomerTypeLabel = (type: CustomerType) => {
    switch (type) {
      case CustomerType.RETAIL:
        return 'Retail'
      case CustomerType.RESELLER:
        return 'Reseller'
      case CustomerType.WHOLESALER:
        return 'Wholesaler'
      case CustomerType.BROKER:
        return 'Broker'
      default:
        return type
    }
  }

  // Customer columns
  const customerColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Customer Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getCustomerTypeIcon(params.row.customer_type)}
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'customer_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getCustomerTypeLabel(params.value)}
          color={
            params.value === CustomerType.RETAIL ? 'default' :
            params.value === CustomerType.RESELLER ? 'primary' :
            params.value === CustomerType.WHOLESALER ? 'secondary' : 'info'
          }
        />
      ),
    },
    {
      field: 'customer_group',
      headerName: 'Group',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.customer_group?.name || 'No Group'}
        </Typography>
      ),
    },
    {
      field: 'mobile_no',
      headerName: 'Mobile',
      width: 130,
    },
    {
      field: 'credit_limit',
      headerName: 'Credit Limit',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          ₹{params.value?.toLocaleString() || '0'}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="ledger"
          icon={<LedgerIcon />}
          label="View Ledger"
          onClick={() => handleViewLedger(params.row)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditCustomer(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteCustomer(params.row.id)}
        />,
      ],
    },
  ]

  // Customer Group columns
  const groupColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Group Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'default_credit_limit',
      headerName: 'Default Credit Limit',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          ₹{params.value?.toLocaleString() || '0'}
        </Typography>
      ),
    },
    {
      field: 'default_credit_days',
      headerName: 'Credit Days',
      width: 120,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditGroup(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteGroup(params.row.id)}
        />,
      ],
    },
  ]

  // Pricing Tier columns
  const pricingColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Tier Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'margin_percent',
      headerName: 'Margin %',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? `${params.value}%` : '-'}
        </Typography>
      ),
    },
    {
      field: 'margin_amount',
      headerName: 'Margin Amount',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? `₹${params.value.toLocaleString()}` : '-'}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditPricingTier(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeletePricingTier(params.row.id)}
        />,
      ],
    },
  ]

  // Event handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    // Tab change handler - currently not used
    console.log('Tab changed to:', newValue)
  }

  const handleEditCustomer = (customer: Customer) => {
    navigate(`/admin/parties/customers/${customer.id}/edit`)
  }

  const handleViewLedger = (customer: Customer) => {
    navigate(`/admin/parties/ledgers?customerId=${customer.id}`)
  }

  const handleDeleteCustomer = async (id: string) => {
    // Find the customer to get their details
    const customer = customersData?.items.find((c: Customer) => c.id === id)
    if (!customer) {
      enqueueSnackbar('Customer not found', { variant: 'error' })
      return
    }
    
    // Fetch customer balance (you may need to add an API endpoint for this)
    // For now, we'll use 0 as placeholder - in production, fetch from ledger
    setCustomerBalance(0)
    setCustomerToDeactivate(customer)
    setDeactivateDialogOpen(true)
  }
  
  const confirmDeactivateCustomer = async () => {
    if (!customerToDeactivate) return
    
    try {
      await deleteCustomer(customerToDeactivate.id).unwrap()
      enqueueSnackbar('Customer deactivated successfully', { variant: 'success' })
      setDeactivateDialogOpen(false)
      setCustomerToDeactivate(null)
      refetchCustomers()
    } catch (error) {
      enqueueSnackbar('Failed to deactivate customer', { variant: 'error' })
    }
  }

  const handleEditGroup = (_group: any) => {
    // navigate(`/admin/parties/customer-groups/edit/${group.id}`)
    enqueueSnackbar('Customer groups feature coming soon', { variant: 'info' })
  }

  const handleDeleteGroup = async (_id: string) => {
    enqueueSnackbar('Customer groups feature coming soon', { variant: 'info' })
  }

  const handleEditPricingTier = (_tier: any) => {
    // navigate(`/customers/pricing-tiers/edit/${tier.id}`)
    enqueueSnackbar('Pricing tiers feature coming soon', { variant: 'info' })
  }

  const handleDeletePricingTier = async (_id: string) => {
    enqueueSnackbar('Pricing tiers feature coming soon', { variant: 'info' })
  }

  // Party Management Handlers
  const handleEditAddress = (party: any, type: 'billing' | 'shipping') => {
    setSelectedParty(party)
    setAddressType(type)
    
    if (type === 'billing') {
      setEditingAddress(party.billing_address)
      setAddressModalOpen(true)
    } else {
      setShippingModalOpen(true)
    }
  }

  const handleEditNotifications = (party: any) => {
    setSelectedParty(party)
    setNotificationModalOpen(true)
  }

  const handleAddShippingAddress = (party: any) => {
    setSelectedParty(party)
    setEditingAddress(null)
    setAddressType('shipping')
    setAddressModalOpen(true)
  }

  const handleSaveAddress = async (address: any) => {
    if (!selectedParty) return
    
    try {
      if (addressType === 'billing') {
        await PartyAddressService.updateCustomerBillingAddress(selectedParty.id, address)
        enqueueSnackbar('Billing address updated successfully', { variant: 'success' })
      } else {
        if (editingAddress?.id) {
          await PartyAddressService.updateCustomerShippingAddress(
            selectedParty.id,
            editingAddress.id,
            address
          )
          enqueueSnackbar('Shipping address updated successfully', { variant: 'success' })
        } else {
          await PartyAddressService.addCustomerShippingAddress(selectedParty.id, address)
          enqueueSnackbar('Shipping address added successfully', { variant: 'success' })
        }
      }
      
      // Refresh data
      refetchCustomers()
      setAddressModalOpen(false)
    } catch (error) {
      console.error('Error saving address:', error)
      enqueueSnackbar('Failed to save address', { variant: 'error' })
      throw error
    }
  }

  const handleSaveNotifications = async (preferences: any) => {
    if (!selectedParty) return
    
    try {
      await notificationService.updateCustomerNotifications(selectedParty.id, preferences)
      enqueueSnackbar('Notification preferences updated successfully', { variant: 'success' })
      refetchCustomers()
    } catch (error) {
      console.error('Error saving notifications:', error)
      enqueueSnackbar('Failed to update notification preferences', { variant: 'error' })
      throw error
    }
  }

  const handleSendTest = async (type: 'email' | 'sms' | 'whatsapp') => {
    if (!selectedParty) return
    
    try {
      await notificationService.sendCustomerTestNotification(selectedParty.id, type)
      enqueueSnackbar(`Test ${type} sent successfully`, { variant: 'success' })
    } catch (error) {
      console.error('Error sending test notification:', error)
      enqueueSnackbar(`Failed to send test ${type}`, { variant: 'error' })
    }
  }

  const handleDeleteShippingAddress = async (addressId: string) => {
    if (!selectedParty) return
    
    try {
      await PartyAddressService.deleteCustomerShippingAddress(selectedParty.id, addressId)
      enqueueSnackbar('Shipping address deleted successfully', { variant: 'success' })
      refetchCustomers()
    } catch (error) {
      console.error('Error deleting address:', error)
      enqueueSnackbar('Failed to delete address', { variant: 'error' })
    }
  }

  const handleSetDefaultAddress = async (addressId: string) => {
    if (!selectedParty) return
    
    try {
      await PartyAddressService.setDefaultShippingAddress(selectedParty.id, addressId)
      enqueueSnackbar('Default address updated successfully', { variant: 'success' })
      refetchCustomers()
    } catch (error) {
      console.error('Error setting default address:', error)
      enqueueSnackbar('Failed to set default address', { variant: 'error' })
    }
  }

  // Get available groups for filter
  const availableGroups = useMemo(() => {
    return groupsData?.items || []
  }, [groupsData])

  // Bulk operation handlers
  const handleCustomerGroupUpdate = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Update Customer Groups',
        description: `Updating group for ${selectedIds.length} customers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        let successful = 0
        let failed = 0
        
        for (let i = 0; i < items.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 100))
          
          try {
            // Simulate group update API call
            successful++
            onProgress(i + 1, `Updated group for customer ${i + 1}`)
          } catch (error) {
            failed++
          }
        }
        
        return { successful, failed, warnings: 0, errors: [] }
      }
    )
    setSelectedCustomers([])
  }

  const handleCustomerActivate = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Activate Customers',
        description: `Activating ${selectedIds.length} customers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Starting bulk activation...')
          
          const result = await bulkUpdateCustomerStatus({
            customer_ids: items,
            is_active: true,
          }).unwrap()
          
          onProgress(items.length, 'Bulk activation completed')
          
          // Refetch customers to show updated data
          refetchCustomers()
          
          enqueueSnackbar(`Successfully activated ${result.successful.length} customers`, { variant: 'success' })
          if (result.failed.length > 0) {
            enqueueSnackbar(`Failed to activate ${result.failed.length} customers`, { variant: 'warning' })
          }
          
          return {
            successful: result.successful.length,
            failed: result.failed.length,
            warnings: 0,
            errors: result.failed.map(f => f.error || 'Unknown error'),
          }
        } catch (error: any) {
          enqueueSnackbar(error?.data?.detail || 'Failed to activate customers', { variant: 'error' })
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.data?.detail || 'Failed to activate customers'],
          }
        }
      }
    )
    setSelectedCustomers([])
  }

  const handleCustomerDeactivate = async (selectedIds: string[]) => {
    // Confirm deactivation
    if (!window.confirm(`Are you sure you want to deactivate ${selectedIds.length} customers?`)) {
      return
    }

    await executeBulkOperation(
      selectedIds,
      {
        title: 'Deactivate Customers',
        description: `Deactivating ${selectedIds.length} customers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Starting bulk deactivation...')
          
          const result = await bulkUpdateCustomerStatus({
            customer_ids: items,
            is_active: false,
          }).unwrap()
          
          onProgress(items.length, 'Bulk deactivation completed')
          
          // Refetch customers to show updated data
          refetchCustomers()
          
          enqueueSnackbar(`Successfully deactivated ${result.successful.length} customers`, { variant: 'success' })
          if (result.failed.length > 0) {
            enqueueSnackbar(`Failed to deactivate ${result.failed.length} customers`, { variant: 'warning' })
          }
          
          return {
            successful: result.successful.length,
            failed: result.failed.length,
            warnings: 0,
            errors: result.failed.map(f => f.error || 'Unknown error'),
          }
        } catch (error: any) {
          enqueueSnackbar(error?.data?.detail || 'Failed to deactivate customers', { variant: 'error' })
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.data?.detail || 'Failed to deactivate customers'],
          }
        }
      }
    )
    setSelectedCustomers([])
  }

  const handleCustomerExport = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Export Customers',
        description: `Exporting ${selectedIds.length} customers to CSV`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        try {
          onProgress(0, 'Preparing export...')
          
          const blob = await bulkExportCustomers({
            customer_ids: items,
            format: 'csv',
          }).unwrap()
          
          onProgress(items.length, 'Export completed, downloading file...')
          
          // Create download link
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          const timestamp = new Date().toISOString().split('T')[0]
          a.download = `customers_export_${timestamp}.csv`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          
          enqueueSnackbar(`Successfully exported ${items.length} customers`, { variant: 'success' })
          
          return {
            successful: items.length,
            failed: 0,
            warnings: 0,
            errors: [],
          }
        } catch (error: any) {
          enqueueSnackbar(error?.data?.detail || 'Failed to export customers', { variant: 'error' })
          return {
            successful: 0,
            failed: items.length,
            warnings: 0,
            errors: [error?.data?.detail || 'Failed to export customers'],
          }
        }
      }
    )
    setSelectedCustomers([])
  }

  const handleCustomerDelete = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Delete Customers',
        description: `Deleting ${selectedIds.length} customers`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        let successful = 0
        let failed = 0
        const errors: string[] = []
        
        for (let i = 0; i < items.length; i++) {
          const customerId = items[i]
          
          try {
            // Actually call the delete API
            await deleteCustomer(customerId).unwrap()
            successful++
            onProgress(i + 1, `Deleted customer ${i + 1}`)
          } catch (error: any) {
            failed++
            const errorMsg = error?.data?.detail || 'Failed to delete customer'
            errors.push(`Customer ${i + 1}: ${errorMsg}`)
            onProgress(i + 1, `Failed to delete customer ${i + 1}`)
          }
          
          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        
        // Refetch customers after bulk delete
        refetchCustomers()
        
        return { successful, failed, warnings: 0, errors }
      }
    )
    setSelectedCustomers([])
  }

  const customerBulkActions = getCustomerBulkActions(
    handleCustomerGroupUpdate,
    handleCustomerActivate,
    handleCustomerDeactivate,
    handleCustomerExport,
    handleCustomerDelete
  )

  return (
    <Box>
      {/* Progress Manager for bulk operations */}
      <ProgressManager
        operations={operations}
        onClearCompleted={clearCompletedOperations}
        onClearAll={clearAllOperations}
      />

      <Paper sx={{ width: '100%', mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        {/* Bulk Action Bar */}
        <BulkActionBar
          selectedItems={selectedCustomers}
          totalItems={customersData?.items?.length || 0}
          onSelectAll={(selected) => {
            if (selected) {
              setSelectedCustomers(customersData?.items?.map(c => c.id) || [])
            } else {
              setSelectedCustomers([])
            }
          }}
          onClearSelection={() => setSelectedCustomers([])}
          actions={customerBulkActions}
          itemName="customers"
        />

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                inputProps={{
                  'aria-label': 'Search customers by name, email, phone, or GSTIN',
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="customer-type-filter-label">Type</InputLabel>
                <Select
                  labelId="customer-type-filter-label"
                  value={customerTypeFilter}
                  onChange={(e) => setCustomerTypeFilter(e.target.value)}
                  input={<OutlinedInput label="Type" />}
                  aria-label="Filter customers by type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value={CustomerType.RETAIL}>Retail</MenuItem>
                  <MenuItem value={CustomerType.RESELLER}>Reseller</MenuItem>
                  <MenuItem value={CustomerType.WHOLESALER}>Wholesaler</MenuItem>
                  <MenuItem value={CustomerType.BROKER}>Broker</MenuItem>
                </Select>
              </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="customer-group-filter-label">Group</InputLabel>
                  <Select
                    labelId="customer-group-filter-label"
                    value={customerGroupFilter}
                    onChange={(e) => setCustomerGroupFilter(e.target.value)}
                    input={<OutlinedInput label="Group" />}
                    aria-label="Filter customers by group"
                  >
                    <MenuItem value="">All Groups</MenuItem>
                    {availableGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="customer-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="customer-status-filter-label"
                    value={customerActiveFilter}
                    onChange={(e) => setCustomerActiveFilter(e.target.value)}
                    input={<OutlinedInput label="Status" />}
                    aria-label="Filter customers by status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                    sx={{ mr: 1 }}
                    aria-label="View mode"
                  >
                    <ToggleButton value="table" aria-label="table view">
                      <Tooltip title="Table View">
                        <ViewList />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="card" aria-label="card view">
                      <Tooltip title="Card View">
                        <ViewModule />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/parties/customers/create')}
                    aria-label="Add new customer"
                    sx={{ 
                      textTransform: 'none', 
                      fontWeight: 500, 
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      }
                    }}
                  >
                    Add Customer
                  </Button>
                  <Tooltip title="Refresh customer list">
                    <IconButton 
                      onClick={() => refetchCustomers()}
                      aria-label="Refresh customer list"
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>

            {customersError && (
              <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                Failed to load customers. Please try again.
              </Alert>
            )}

            {viewMode === 'table' ? (
              <DataGrid
                rows={customersData?.items || []}
                columns={customerColumns}
                loading={customersLoading}
                paginationMode="server"
                rowCount={customersData?.total || 0}
                paginationModel={{ page: customerPage, pageSize: customerPageSize }}
                onPaginationModelChange={(model) => {
                  setCustomerPage(model.page)
                  setCustomerPageSize(model.pageSize)
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                checkboxSelection
                rowSelectionModel={selectedCustomers}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectedCustomers(newSelection as string[])
                }}
                disableRowSelectionOnClick
                autoHeight
                sx={{ minHeight: 400, mt: 2 }}
                aria-label="Customers data grid"
                getRowId={(row) => row.id}
              />
            ) : (
              <Grid container spacing={3} sx={{ mt: 2 }}>
                {customersData?.items?.map((customer) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={customer.id}>
                    <PartyCard
                      party={{
                        ...customer,
                        type: 'customer' as const,
                        is_active: customer.is_active ?? true,
                        billing_address: (customer.billing_address && typeof customer.billing_address === 'object' && 'address_line1' in customer.billing_address)
                          ? customer.billing_address as any
                          : {
                              address_line1: '',
                              city: '',
                              state: '',
                              pincode: '',
                              country: 'India'
                            },
                        shipping_addresses: Array.isArray(customer.shipping_addresses) 
                          ? customer.shipping_addresses.filter((addr: any) => addr && typeof addr === 'object' && 'address_line1' in addr) as any[]
                          : []
                      }}
                      onEdit={(party) => handleEditCustomer(party as unknown as Customer)}
                      onDelete={(id) => handleDeleteCustomer(id)}
                      onViewLedger={(party) => handleViewLedger(party as unknown as Customer)}
                      onEditAddress={handleEditAddress}
                      onEditNotifications={handleEditNotifications}
                      onAddShippingAddress={handleAddShippingAddress}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Paper>

      {/* Deactivation Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
      >
        <DialogTitle id="deactivate-dialog-title">Deactivate Customer</DialogTitle>
        <DialogContent>
          <DialogContentText id="deactivate-dialog-description">
            Are you sure you want to deactivate <strong>{customerToDeactivate?.name}</strong>?
          </DialogContentText>
          {customerBalance > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This customer has an outstanding balance of ₹{customerBalance.toFixed(2)}. 
              Please ensure all payments are settled before deactivation.
            </Alert>
          )}
          <DialogContentText sx={{ mt: 2 }}>
            Deactivating this customer will:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Prevent new transactions from being created</li>
            <li>Preserve all historical data and records</li>
            <li>Allow reactivation at any time</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeactivateDialogOpen(false)}
            aria-label="Cancel deactivation"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeactivateCustomer} 
            color="error" 
            variant="contained"
            aria-label="Confirm deactivation"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Edit Modal */}
      <AddressEditModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        address={editingAddress}
        addressType={addressType}
        partyName={selectedParty?.name || ''}
        onSave={handleSaveAddress}
      />

      {/* Notification Edit Modal */}
      <NotificationEditModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        partyName={selectedParty?.name || ''}
        email={selectedParty?.email}
        mobile={selectedParty?.mobile_no}
        preferences={selectedParty?.communication_preferences}
        onSave={handleSaveNotifications}
        onSendTest={handleSendTest}
      />

      {/* Shipping Addresses Modal */}
      <ShippingAddressesModal
        open={shippingModalOpen}
        onClose={() => setShippingModalOpen(false)}
        partyName={selectedParty?.name || ''}
        addresses={selectedParty?.shipping_addresses || []}
        onAdd={handleSaveAddress}
        onEdit={async (addressId, address) => {
          setEditingAddress({ ...address, id: addressId })
          setShippingModalOpen(false)
          setAddressModalOpen(true)
        }}
        onDelete={handleDeleteShippingAddress}
        onSetDefault={handleSetDefaultAddress}
      />
    </Box>
  )
}

export default CustomersMUI